import {
  AccountInfo,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import * as microsoftTeams from "@microsoft/teams-js";

// Check if mock auth is enabled (defaults to true in development)
// Mock auth is enabled when DEV mode is true OR explicitly enabled via env var
const isMockAuthEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_MOCK_AUTH === "true";
const DEBUG_AUTH = import.meta.env.VITE_DEBUG_AUTH === "true";

// Teams/SharePoint context detection
let isTeamsContext = false;
let isSharePointContext = false;
let teamsInitialized = false;

// Timeout for Teams SDK initialization (2 seconds)
const TEAMS_INIT_TIMEOUT_MS = 2000;

/**
 * Helper to create a timeout promise
 */
function createTimeoutPromise<T>(ms: number, errorMessage: string): Promise<T> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(errorMessage));
    }, ms);
  });
}

/**
 * Detect if running inside Microsoft Teams or SharePoint iframe
 * Optimized with timeout to prevent long delays in non-Teams environments
 */
export async function detectHostContext(): Promise<{
  isTeams: boolean;
  isSharePoint: boolean;
}> {
  // Check if we're in an iframe (Teams or SharePoint)
  const inIframe = window.self !== window.top;

  // Check for SharePoint context first (fast check)
  if (inIframe) {
    const ancestorOrigins = window.location.ancestorOrigins as
      | DOMStringList
      | undefined;
    if (ancestorOrigins && ancestorOrigins.length > 0) {
      const ancestors = Array.from(ancestorOrigins);
      isSharePointContext = ancestors.some((origin) =>
        origin.includes(".sharepoint.com")
      );
    }
  }

  // If we detected SharePoint context, skip Teams SDK initialization
  // SharePoint iframe doesn't need Teams SDK and it would just timeout
  if (isSharePointContext) {
    console.info("📄 Running in SharePoint iframe context - skipping Teams SDK");
    teamsInitialized = true; // Mark as done so we don't retry
    return { isTeams: false, isSharePoint: true };
  }

  // Try to initialize Teams SDK with timeout
  if (!teamsInitialized) {
    try {
      // Race between Teams init and timeout
      await Promise.race([
        microsoftTeams.app.initialize(),
        createTimeoutPromise<undefined>(TEAMS_INIT_TIMEOUT_MS, "Teams SDK init timeout"),
      ]);

      // If we get here, Teams SDK initialized successfully
      const context = await Promise.race([
        microsoftTeams.app.getContext(),
        createTimeoutPromise<microsoftTeams.app.Context>(
          TEAMS_INIT_TIMEOUT_MS,
          "Teams context timeout"
        ),
      ]);

      isTeamsContext = true;
      teamsInitialized = true;
      console.info("🏢 Running in Microsoft Teams context", {
        hostClientType: context.app.host.clientType,
        teamId: context.team?.internalId,
      });
    } catch (error) {
      // Not in Teams or timed out - this is expected when running standalone
      isTeamsContext = false;
      teamsInitialized = true;
      if (import.meta.env.DEV) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        console.debug(`Teams SDK detection completed: ${errorMessage}`);
      }
    }
  }

  return { isTeams: isTeamsContext, isSharePoint: isSharePointContext };
}

/**
 * Get Teams SSO token (when running in Teams)
 */
export async function getTeamsSsoToken(): Promise<string | null> {
  if (!isTeamsContext) return null;

  try {
    const token = await microsoftTeams.authentication.getAuthToken();
    return token;
  } catch (error) {
    console.warn(
      "Teams SSO token acquisition failed, falling back to MSAL:",
      error
    );
    return null;
  }
}

/**
 * Check if running in Teams or SharePoint (or any iframe)
 */
export function isEmbeddedContext(): boolean {
  // Direct iframe check as fallback (window.self !== window.top when in iframe)
  const inIframe = window.self !== window.top;
  return isTeamsContext || isSharePointContext || inIframe;
}

export function isInTeams(): boolean {
  return isTeamsContext;
}

export function isInSharePoint(): boolean {
  return isSharePointContext;
}

// Mock user for local development
const mockAccount: AccountInfo = {
  homeAccountId: "mock-user-001",
  localAccountId: "mock-user-001",
  environment: "login.microsoftonline.com",
  tenantId: "mock-tenant-id",
  username: "demo.user@corus.org",
  name: "Demo User",
  idTokenClaims: {
    oid: "mock-user-001",
    preferred_username: "demo.user@corus.org",
    name: "Demo User",
    tid: "mock-tenant-id",
    groups: [
      "AI-Registry-Users",
      "AI-Registry-Champions",
      "AI-Registry-Council",
      "AI-Registry-Executive-Sponsors",
      "AI-Registry-IT-Team",
      "AI-Registry-Admins",
    ], // Give mock user multiple roles for testing
  },
};

// Log warning if required env vars are missing (helps debug deployment issues)
if (!import.meta.env.VITE_AZURE_CLIENT_ID && !import.meta.env.DEV) {
  console.error("[Auth Config] VITE_AZURE_CLIENT_ID is not set - authentication will fail");
}
if (!import.meta.env.VITE_AZURE_TENANT_ID && !import.meta.env.DEV) {
  console.error("[Auth Config] VITE_AZURE_TENANT_ID is not set - authentication will fail");
}

const msalConfig: Configuration = {
  auth: {
    // In production, these MUST be set via VITE_* env vars at build time
    // Fallbacks are only for local development
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "local-dev-client-id",
    authority: `https://login.microsoftonline.com/${
      import.meta.env.VITE_AZURE_TENANT_ID || "common"
    }`,
    redirectUri:
      import.meta.env.VITE_AZURE_REDIRECT_URI || "http://localhost:5173",
    postLogoutRedirectUri:
      import.meta.env.VITE_AZURE_REDIRECT_URI || "http://localhost:5173",
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            if (import.meta.env.DEV) {
              console.info(message);
            }
            break;
          case LogLevel.Verbose:
            if (import.meta.env.DEV) {
              console.debug(message);
            }
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
    allowNativeBroker: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Scopes for API access
// VITE_AZURE_APPLICATION_ID_URI should be the full Application ID URI from Azure AD "Expose an API"
// e.g., "api://gray-mud-0453bc00f.4.azurestaticapps.net/cfe6f8ce-d058-43b6-803b-abe4b418eb22"
const rawAppIdUri = import.meta.env.VITE_AZURE_APPLICATION_ID_URI?.trim();
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;

// Normalize the Application ID URI (add api:// prefix if missing)
const normalizedAppIdUri = rawAppIdUri
  ? rawAppIdUri.startsWith("api://")
    ? rawAppIdUri
    : `api://${rawAppIdUri}`
  : undefined;

// Use Application ID URI for scopes if available, otherwise fall back to client ID
const apiScopeBase = normalizedAppIdUri || (clientId ? `api://${clientId}` : null);

// Log configuration for debugging (only in dev or if missing)
if (!apiScopeBase) {
  console.error("[Auth Config] Missing VITE_AZURE_APPLICATION_ID_URI or VITE_AZURE_CLIENT_ID environment variable");
}

const apiScopes = apiScopeBase
  ? [`${apiScopeBase}/UseCase.Read`, `${apiScopeBase}/UseCase.Write`]
  : [];

export const loginRequest = {
  scopes: apiScopes,
};

export const apiRequest = {
  scopes: apiScopes,
};

// Store Teams SSO token for later use
let teamsSsoToken: string | null = null;

export function getStoredTeamsSsoToken(): string | null {
  return teamsSsoToken;
}

// Initialize MSAL and detect host context (Teams/SharePoint)
export async function initializeMsal(): Promise<void> {
  if (isMockAuthEnabled) {
    if (DEBUG_AUTH) {
      console.info("[auth] Mock authentication enabled for local development");
    }
    return;
  }

  // Detect if running in Teams or SharePoint first
  await detectHostContext();

  await msalInstance.initialize();

  // If in Teams, automatically try to get SSO token
  if (isTeamsContext) {
    console.info("🏢 Detected Teams context, attempting SSO...");
    try {
      const token = await microsoftTeams.authentication.getAuthToken();
      if (token) {
        teamsSsoToken = token;
        console.info("✅ Teams SSO token acquired successfully");
        // The token itself is the authentication - we store it for API calls
        // MSAL account won't be populated from Teams SSO, but we have the token
      }
    } catch (ssoError) {
      console.warn("Teams SSO failed during initialization:", ssoError);
      // Will fall back to showing login button
    }
  }

  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0] ?? null);
  }

  // Log context info
  if (isTeamsContext) {
    console.info("🔐 MSAL initialized with Teams SSO support");
  } else if (isSharePointContext) {
    console.info("🔐 MSAL initialized with SharePoint iframe support");
  }
}

// Token acquisition lock to prevent concurrent attempts
let tokenAcquisitionInProgress = false;
let tokenAcquisitionPromise: Promise<string | null> | null = null;

// Get access token silently (with fallback to redirect)
// Supports Teams SSO when running in Microsoft Teams
export async function getAccessToken(): Promise<string | null> {
  // In mock mode, return a mock token
  if (isMockAuthEnabled) {
    return "mock-access-token-for-local-development";
  }

  // If in Teams, use stored SSO token or try to get a new one
  if (isTeamsContext) {
    // Return stored token if we have one
    if (teamsSsoToken) {
      return teamsSsoToken;
    }
    // Try to get a new token
    const teamsToken = await getTeamsSsoToken();
    if (teamsToken) {
      teamsSsoToken = teamsToken; // Cache for later
      return teamsToken;
    }
    // Fall through to MSAL if Teams SSO fails
  }

  // If token acquisition is already in progress, wait for it
  if (tokenAcquisitionInProgress && tokenAcquisitionPromise) {
    return tokenAcquisitionPromise;
  }

  const account = msalInstance.getActiveAccount();
  console.warn("[getAccessToken] Active account:", account?.username ?? "null", "| Scopes:", apiRequest.scopes);
  if (!account) {
    // In embedded context (Teams/SharePoint), try to get token silently without active account
    if (isEmbeddedContext()) {
      console.warn(
        "No active account in embedded context. User may need to consent."
      );
    }
    console.warn("[getAccessToken] No active account, returning null");
    return null;
  }

  // Set up the acquisition promise
  tokenAcquisitionPromise = (async () => {
    tokenAcquisitionInProgress = true;
    try {
      // Try silent token acquisition first
      console.warn("[getAccessToken] Attempting acquireTokenSilent...");
      const response = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account,
      });
      console.warn("[getAccessToken] Token acquired successfully, length:", response.accessToken.length);
      return response.accessToken;
    } catch (silentError) {
      // Check if it's an interaction_required error (needs user interaction)
      const error = silentError as { errorCode?: string; message?: string };
      console.warn("[getAccessToken] acquireTokenSilent failed:", error.errorCode, error.message);
      if (
        error.errorCode === "interaction_required" ||
        error.errorCode === "consent_required"
      ) {
        // In embedded context, we can't do redirects - use popup
        if (isEmbeddedContext()) {
          try {
            const popupResponse = await msalInstance.acquireTokenPopup({
              ...apiRequest,
              account,
            });
            return popupResponse.accessToken;
          } catch (popupError) {
            console.warn(
              "Popup authentication failed in embedded context:",
              popupError
            );
            return null;
          }
        }
        // Use redirect instead of popup for better reliability in standalone mode
        console.warn(
          "Token acquisition requires user interaction. Please log in again."
        );
        return null;
      }

      // For other errors (like interaction_in_progress), just return null
      if (error.errorCode === "interaction_in_progress") {
        console.warn(
          "Authentication interaction already in progress. Please wait."
        );
        return null;
      }

      console.error("Failed to acquire token silently:", error);
      return null;
    } finally {
      tokenAcquisitionInProgress = false;
      tokenAcquisitionPromise = null;
    }
  })();

  return tokenAcquisitionPromise;
}

// Login with redirect (or popup in embedded context)
export async function login(): Promise<void> {
  if (isMockAuthEnabled) {
    if (DEBUG_AUTH) {
      console.info("[auth] Mock login - redirecting to home");
    }
    // In mock mode, just reload the page - the mock user is always "logged in"
    window.location.href = "/";
    return;
  }

  // In Teams, try Teams SSO authentication flow
  if (isTeamsContext) {
    try {
      const token = await microsoftTeams.authentication.getAuthToken();
      if (token) {
        console.info("🏢 Teams SSO authentication successful");
        // Force reload to pick up the authenticated state
        window.location.reload();
        return;
      }
    } catch (teamsError) {
      console.warn("Teams SSO failed, falling back to MSAL:", teamsError);
    }
  }

  // In embedded context (SharePoint or Teams fallback), use popup
  if (isEmbeddedContext()) {
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      msalInstance.setActiveAccount(response.account);
      console.info("🔐 Popup login successful in embedded context");
      window.location.reload();
    } catch (popupError) {
      console.error("Popup login failed:", popupError);
      throw popupError;
    }
    return;
  }

  // Standard redirect flow for standalone app
  await msalInstance.loginRedirect(loginRequest);
}

// Logout with redirect
export async function logout(): Promise<void> {
  if (isMockAuthEnabled) {
    if (DEBUG_AUTH) {
      console.info("[auth] Mock logout");
    }
    window.location.href = "/login";
    return;
  }

  const account = msalInstance.getActiveAccount();
  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI,
  });
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  if (isMockAuthEnabled) {
    return true; // Always authenticated in mock mode
  }
  // In Teams with SSO token, user is authenticated
  if (isTeamsContext && teamsSsoToken) {
    return true;
  }
  return msalInstance.getActiveAccount() !== null;
}

// Decode JWT token to extract claims (for Teams SSO)
function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// Get current account
export function getActiveAccount(): AccountInfo | null {
  if (isMockAuthEnabled) {
    if (DEBUG_AUTH) {
      console.log("[getActiveAccount] Returning mock account:", {
        name: mockAccount.name,
        idTokenClaimsName: mockAccount.idTokenClaims?.name,
      });
    }
    return mockAccount;
  }

  // If in Teams with SSO token, create account from token claims
  if (isTeamsContext && teamsSsoToken) {
    const claims = decodeJwt(teamsSsoToken);
    if (claims) {
      const teamsAccount: AccountInfo = {
        homeAccountId: (claims.oid as string) || "teams-user",
        localAccountId: (claims.oid as string) || "teams-user",
        environment: "login.microsoftonline.com",
        tenantId: (claims.tid as string) || "",
        username:
          (claims.preferred_username as string) || (claims.upn as string) || "",
        name: (claims.name as string) || "",
        idTokenClaims: claims,
      };
      if (import.meta.env.DEV) {
        console.log("[getActiveAccount] Returning Teams SSO account:", {
          name: teamsAccount.name,
          username: teamsAccount.username,
        });
      }
      return teamsAccount;
    }
  }

  // Try to get active account from MSAL
  let account = msalInstance.getActiveAccount();

  // If no active account but accounts exist, set the first one as active
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0] ?? null;
      if (account) {
        msalInstance.setActiveAccount(account);
        if (DEBUG_AUTH) {
          console.log("[getActiveAccount] Set first account as active:", {
            name: account.name,
            idTokenClaimsName: account.idTokenClaims?.name,
          });
        }
      }
    }
  }

  if (DEBUG_AUTH) {
    if (account) {
      console.log("[getActiveAccount] Returning MSAL account:", {
        name: account.name,
        idTokenClaimsName: account.idTokenClaims?.name,
      });
    } else {
      console.warn(
        "[getActiveAccount] No account found, accounts:",
        msalInstance.getAllAccounts().length
      );
    }
  }

  return account;
}

// Export mock mode flag
export { isMockAuthEnabled };

/**
 * Warm up the API by calling the health endpoint
 * This helps reduce cold start delays for Azure Functions
 * Called early during app initialization to start warming up the API
 */
export function warmUpApi(): void {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
    // Fire and forget - don't wait for response
    fetch(`${apiBaseUrl}/health`, {
      method: "GET",
      // Short timeout - we just want to trigger the cold start
      signal: AbortSignal.timeout(5000),
    }).catch(() => {
      // Silently ignore errors - this is just a warmup
    });
    console.info("🔥 API warmup request sent");
  } catch {
    // Silently ignore errors - this is just a warmup
  }
}
